import React from "react";
import { Badge, Button, Stack, Text } from "@mantine/core";
import { IconSettings } from "@tabler/icons-react";
import moment from "moment";
import { SettingsField } from "../common";
import { useSalon } from "../../../context/SalonContext";
import { useFeatureAccess } from "../../../context/FeatureAccessContext";

const STRIPE_BILLING_URL = process.env.REACT_APP_STRIPE_BILLING_URL;

const statusColor = (status?: string): string => {
  switch (status) {
    case "Active":
      return "green";
    case "Trial":
      return "var(--brand-teal)";
    case "Inactive":
      return "red";
    default:
      return "gray";
  }
};

export interface BillingStatusTabProps {
  businessInfo: any;
}

export default function BillingStatusTab({
  businessInfo,
}: BillingStatusTabProps) {
  const { currentUser } = useSalon() as { currentUser: any };
  const { access } = useFeatureAccess();
  const isOwner = access?.user_level === "Owner";
  const status = businessInfo?.status;

  const shouldShowTrialDate = () => {
    if (!businessInfo?.free_trial_end_date) return false;
    const trialEndDate = moment(businessInfo.free_trial_end_date);
    const today = moment().startOf("day");
    // For Active accounts only surface the date while the trial hasn't lapsed.
    if (status === "Active") return trialEndDate.isAfter(today);
    return true;
  };

  const handleManageSubscription = () => {
    const encodedEmail = encodeURIComponent(currentUser?.email);
    window.open(
      `${STRIPE_BILLING_URL}?prefilled_email=${encodedEmail}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const showManage = status === "Active" && isOwner;

  return (
    <Stack gap={0}>
      <SettingsField label="Current Status" withDivider={shouldShowTrialDate()}>
        <Badge color={statusColor(status)} variant="light">
          {status || "Unknown"}
        </Badge>
      </SettingsField>

      {shouldShowTrialDate() && (
        <SettingsField label="Free trial ends on" withDivider={false}>
          {moment(businessInfo?.free_trial_end_date).format("MMMM Do, YYYY")}
        </SettingsField>
      )}

      {showManage && (
        <Stack gap="sm" mt="lg">
          <Text size="sm" c="dimmed">
            View and update your subscription details with the button below. You
            can also view and download your invoices in the Subscription
            Management page.
          </Text>
          <Button
            onClick={handleManageSubscription}
            variant="light"
            color="var(--brand-teal)"
            leftSection={<IconSettings size={18} />}
            w={{ base: "100%", sm: "auto" }}
            style={{ alignSelf: "flex-start" }}
          >
            Manage Existing Subscription
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
